// HoursPage (timesheet rules §5): the team's pay period. PeriodHead (the period, the pay line, the
// facts: to fix, paid by the team, past 8 h a day or 40 h a week); To fix (everyone's, missed
// clock-outs first: ToFixList with the person, each first fix opening the quick edit with it chosen);
// People (rows on hairlines: the name, a link to their Timesheet, and "Working now" while a part of
// theirs runs; paid, at 1.5×, at 2× only when anyone has it, to fix in red above 0); Lock for payroll
// (every reason it's off in the server's words, the start dates to confirm, then Prepare and lock
// through a ConfirmDialog that starts on Not yet; a manager sees the reasons and who locks it); and
// the team's latest changes, an area like the rest even when there are none. Until there's a pay period
// (QA-757) it's a week: ‹ This week › instead of the periods, one quiet line saying periods and locking
// come with payroll (a link to set it up for those who may), and no Lock for payroll. Minutes only, never pay.
// Reads only --hpage-*; what it's built from reads its own.
import { useState } from 'react';
import { Area } from '../../components/Area/Area.tsx';
import { Button } from '../../components/Button/Button.tsx';
import { Checkbox } from '../../components/Checkbox/Checkbox.tsx';
import { ConfirmDialog } from '../../components/Dialog/Dialog.tsx';
import { HistoryFeed } from '../../components/HistoryFeed/HistoryFeed.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { PenKey } from '../../components/PenKey/PenKey.tsx';
import { Segmented, type Segment } from '../../components/Segmented/Segmented.tsx';
import { CHANGE_FILTERS, changeEntries } from '../changes.tsx';
import { PayrollLine, PeriodHead, WeekStep, type WeekStepProps } from '../PeriodHead/PeriodHead.tsx';
import type { PageNotice } from '../TimesheetPage/TimesheetPage.tsx';
import type { HoursView, ToFixRow } from '../timesheet-view.ts';
import { ToFixList } from '../ToFixList/ToFixList.tsx';
import './HoursPage.css';

export interface HoursPageProps {
  view: HoursView | null;
  notice?: PageNotice;
  today: string;
  periods: Segment[];
  periodId: string;
  onPeriod: (periodId: string) => void;
  /** A To fix row: the quick edit on that person's day, with its first fix chosen or not. */
  onOpenDay: (row: ToFixRow, fixId: string | null) => void;
  /** A name: their Timesheet on this period. */
  onPerson: (staffId: string) => void;
  /** The start dates confirmed so far, and a change to them. */
  confirmed: string[];
  onConfirm: (staffId: string, isConfirmed: boolean) => void;
  /** Prepare and lock; a rejection keeps the question open with its words. */
  onLock: () => Promise<void> | void;
  /** Payroll ›, once it's locked (Books › Payroll). */
  onPayroll?: () => void;
  /** No pay period yet: the weeks instead of the periods. */
  week?: WeekStepProps;
  /** No pay period yet: the quiet line's Set up payroll › (Books › Payroll), for those who may. */
  onSetUpPayroll?: () => void;
  /** Pens by person, for the People rows. */
  pens?: Record<string, 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8>;
  /** Storybook: the lock's question already open. */
  previewAsk?: boolean;
}

export function HoursPage(props: HoursPageProps) {
  const { view, notice, today, onOpenDay, onPerson } = props;
  const [asking, setAsking] = useState(props.previewAsk ?? false);
  if (!view || notice)
    return (
      <div className="fl-hpage">
        <PeriodHead title={notice?.title ?? 'Hours'} />
        <p className="fl-hpage__notice" role="status">
          {notice?.words ?? 'Loading the team’s hours…'}{' '}
          {notice?.action ? <Link onPress={notice.action.onPress}>{notice.action.label}</Link> : null}
        </p>
      </div>
    );
  const lock = view.lock;
  const fixes = view.toFix.length || view.toFixCounts.length;
  return (
    <div className="fl-hpage">
      <PeriodHead
        title={view.title}
        payLine={
          view.noPayroll ? <PayrollLine line={view.noPayroll} onSetUp={props.onSetUpPayroll} /> : view.payLine
        }
        facts={view.facts}
        titleId="hpage-title"
        actions={
          props.week ? (
            <WeekStep {...props.week} />
          ) : (
            <Segmented
              label="Pay period"
              items={props.periods}
              selectedKey={props.periodId}
              onSelectionChange={props.onPeriod}
            />
          )
        }
      />
      {fixes ? (
        <Area title="To fix" count={view.toFix.length || undefined} defaultOpen>
          <ToFixList
            rows={view.toFix}
            showPerson
            counts={view.toFixCounts}
            onFix={(r) => onOpenDay(r, r.fix.fixId)}
            onOpenDay={(r) => onOpenDay(r, null)}
            onPerson={onPerson}
          />
        </Area>
      ) : null}
      <Area
        title="People"
        count={view.people.length}
        defaultOpen
        emptyLine={props.week ? 'No one has hours this week.' : 'No one has hours in this period.'}
      >
        {view.people.length ? (
          <table className="fl-hpage__table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Paid</th>
                <th scope="col">At 1.5×</th>
                {view.showAt2 ? <th scope="col">At 2×</th> : null}
                <th scope="col">To fix</th>
              </tr>
            </thead>
            <tbody>
              {view.people.map((p) => (
                <tr key={p.staffId}>
                  <th scope="row">
                    <PenKey pen={props.pens?.[p.staffId] ?? null}>
                      <Link onPress={() => onPerson(p.staffId)}>{p.name}</Link>
                    </PenKey>
                    {p.now ? <span className="fl-hpage__now">{p.now}</span> : null}
                  </th>
                  <td>{p.paid}</td>
                  <td>{p.at15}</td>
                  {view.showAt2 ? <td>{p.at2}</td> : null}
                  <td data-tone={p.toFix.tone}>{p.toFix.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Area>
      {lock ? (
        <Area title="Lock for payroll" defaultOpen>
          <div className="fl-hpage__lock">
            {lock.locked ? <p className="fl-hpage__locked">{lock.locked}</p> : null}
            {lock.reasons.length ? (
              <ul className="fl-hpage__reasons">
                {lock.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            ) : null}
            {lock.confirm.length ? (
              <div className="fl-hpage__confirm" role="group" aria-label="Start dates to confirm">
                {lock.confirm.map((c) => (
                  <Checkbox
                    key={c.staffId}
                    isSelected={props.confirmed.includes(c.staffId)}
                    onChange={(on) => props.onConfirm(c.staffId, on)}
                  >
                    {c.words}
                  </Checkbox>
                ))}
              </div>
            ) : null}
            {lock.button ? (
              <p className="fl-hpage__act">
                <Button variant="primary" isDisabled={lock.button.isDisabled} onPress={() => setAsking(true)}>
                  {lock.button.label}
                </Button>
              </p>
            ) : null}
            {lock.who ? (
              <p className="fl-hpage__who">
                {lock.who}
                {lock.locked && props.onPayroll ? (
                  <>
                    {' '}
                    <Link onPress={props.onPayroll}>Payroll ›</Link>
                  </>
                ) : null}
              </p>
            ) : null}
            {lock.button ? (
              <ConfirmDialog
                isOpen={asking}
                onClose={() => setAsking(false)}
                title={`${lock.button.label}?`}
                confirmLabel={lock.button.label}
                cancelLabel={lock.button.notYet}
                startOnCancel
                onConfirm={() => props.onLock()}
                failText="It didn’t lock. Nothing changed; try again."
              >
                {lock.button.ask}
              </ConfirmDialog>
            ) : null}
          </div>
        </Area>
      ) : null}
      {/* opens and closes like the areas above it, so its name lines up with theirs, chevron and all */}
      <Area title="Recent changes" count={view.changes.length} defaultOpen>
        {view.changes.length ? (
          <HistoryFeed
            entries={changeEntries(view.changes)}
            filters={CHANGE_FILTERS}
            today={today}
            searchLabel="Search the changes"
          />
        ) : (
          <p className="fl-hpage__none">No changes yet.</p>
        )}
      </Area>
    </div>
  );
}
